import React from "react";
import {
  Button,
  Col,
  ControlLabel,
  FormControl,
  FormGroup,
  Grid,
  Row,
} from "react-bootstrap";
import UploadBatchModal from "./uploadBatchModal/UploadBatchModal";
import { formatDate, getStoredUser, isSuperAdmin } from "./callWorkspace.utils";
import "./callWorkspace.css";

const CallWorkspace = (p) => (
  <main className="cw-page">
    <Grid fluid>
      <div className="cw-header">
        <div>
          <p className="cw-kicker">CALL OPERATIONS</p>
          <h1>Batch Management</h1>
          <p>View uploaded call batches and assign it to a caller.</p>
        </div>
        <div className="cw-table-actions">
          {isSuperAdmin(getStoredUser()) ? <Button onClick={p.onManageCallTypes}>Manage call types</Button> : null}
          <Button bsStyle="primary" className="cw-primary" onClick={p.onShowUpload}>Upload batch</Button>
        </div>
      </div>
      {p.message ? (
        <div className="cw-alert cw-alert-success" role="status">
          <span>{p.message}</span>
          <button
            type="button"
            aria-label="Dismiss message"
            onClick={p.onDismissMessage}
          >
            ×
          </button>
        </div>
      ) : null}
      {p.error && !p.showUpload && !p.selectedBatch ? (
        <div className="cw-alert cw-alert-error" role="alert">
          <span>{p.error}</span>
          <button type="button" aria-label="Retry loading batches" onClick={() => p.onPageChange(p.pagination.current_page || 1)}>Retry</button>
        </div>
      ) : null}
      <section className="cw-panel">
        <form onSubmit={p.onApplyFilters} noValidate>
          <Row>
            <Col md={5}>
              <FormGroup>
                <ControlLabel>Batch name</ControlLabel>
                <FormControl
                  name="search"
                  value={p.filters.search}
                  onChange={p.onFilterChange}
                  placeholder="Search batch name"
                />
              </FormGroup>
            </Col>
            <Col md={4}>
              <FormGroup>
                <ControlLabel>Campaign date</ControlLabel>
                <FormControl
                  type="date"
                  name="campaignDate"
                  value={p.filters.campaignDate}
                  onChange={p.onFilterChange}
                />
              </FormGroup>
            </Col>
            <Col md={3}>
              <div className="cw-filter-actions">
                <Button type="submit" bsStyle="primary">
                  Apply filters
                </Button>
                <Button type="button" onClick={p.onClearFilters}>
                  Clear
                </Button>
              </div>
            </Col>
          </Row>
        </form>
      </section>
      <section className="cw-panel cw-table-panel">
        <div className="cw-section-heading">
          <div>
            <h2>Batches</h2>
            <p>Select a batch to view its leads and manage assignments.</p>
          </div>
        </div>
        <div
          className="cw-table-wrap"
          tabIndex="0"
          aria-label="Call workspace batches"
        >
          <table className="cw-table">
            <thead>
              <tr>
                <th>Batch name</th>
                <th>Campaign date</th>
                <th>Total leads</th>
                <th>Not called</th>
                <th>Follow-up</th>
                <th>Completed</th>
                <th>Don't Call</th>
                <th>Uploader</th>
                <th>Created</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {p.loading ? (
                <tr><td colSpan="10" className="cw-table-state">Loading batches…</td></tr>
              ) : p.batches.length ? (
                p.batches.map((batch) => (
                  <tr key={batch.id}>
                    <td>
                      <strong>{batch.name}</strong>
                    </td>
                    <td>{formatDate(batch.campaign_date)}</td>
                    <td>{batch.total_leads}</td>
                    <td>{batch.not_called}</td>
                    <td>{batch.follow_up}</td>
                    <td>{batch.completed}</td>
                    <td>{batch.blacklisted}</td>
                    <td>{batch.uploader || "—"}</td>
                    <td>{formatDate(batch.uploaded_at)}</td>
                    <td>
                      <div className="cw-table-actions">
                        <Button
                          type="button"
                          bsStyle="primary"
                          onClick={() => p.onViewBatch(batch)}
                        >
                          View leads
                        </Button>
                        <Button
                          type="button"
                          bsStyle="default"
                          onClick={() => p.onSelectBatch(batch)}
                        >
                          Assign batch
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="10" className="cw-table-state">
                    <strong>No batches found</strong>
                    <span>
                      Change the filters or upload a batch to get started.
                    </span>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
        <div className="cw-pagination" aria-label="Batch pages">
          <span>{p.pagination.total} batches</span>
          <div>
            <Button type="button" disabled={p.pagination.current_page <= 1 || p.loading} onClick={() => p.onPageChange(p.pagination.current_page - 1)}>Previous</Button>
            <span>Page {p.pagination.current_page} of {p.pagination.last_page}</span>
            <Button type="button" disabled={p.pagination.current_page >= p.pagination.last_page || p.loading} onClick={() => p.onPageChange(p.pagination.current_page + 1)}>Next</Button>
          </div>
        </div>
      </section>
    </Grid>
    <UploadBatchModal
      show={p.showUpload}
      onHide={p.onHideUpload}
      onUpload={p.onUpload}
      upload={p.upload}
      uploadUsers={p.uploadUsers}
      users={p.users}
      callTypes={p.callTypes}
      callTypesError={p.callTypesError}
      batchCallType={p.batchCallType}
      onRetryCallTypes={p.onRetryCallTypes}
      usersError={p.usersError}
      onRetryUsers={p.onRetryUsers}
      onToggleUploadUser={p.onToggleUploadUser}
      onUploadChange={p.onUploadChange}
      usersLoading={p.usersLoading}
      uploading={p.uploading}
      assigning={false}
      error={p.showUpload ? p.error : ""}
    />
    <UploadBatchModal
      usersOnly
      show={!!p.selectedBatch}
      batchName={p.selectedBatch ? p.selectedBatch.name : ""}
      onHide={p.onCloseAssignment}
      onUpload={p.onAssignBatch}
      upload={p.upload}
      uploadUsers={p.uploadUsers}
      users={p.users}
      usersError={p.usersError}
      onRetryUsers={p.onRetryUsers}
      onToggleUploadUser={p.onToggleUploadUser}
      onUploadChange={p.onUploadChange}
      usersLoading={p.usersLoading}
      uploading={false}
      assigning={p.assigning}
      error={p.selectedBatch ? p.error : ""}
    />
  </main>
);
export default CallWorkspace;
